import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DocusealForm } from '@docuseal/react'

import { apiRequest, ENVIRONMENT, getCsrfToken, getLanguage, PANADATA_ROOT, customDocusealCss } from '../../utils';

import { Modal } from 'bootstrap';
import { KybDescription } from './KybDescription';

import descriptionIcon from '../../assets/icons/description.svg?raw&inline';
import progressIcon from '../../assets/icons/progress.svg?raw&inline';
import checkCircleIcon from '../../assets/icons/check_circle.svg?raw&inline';
import chevronRightIcon from '../../assets/icons/chevron_right.svg?raw&inline';

export const PrivacyPolicies: React.FC<PrivacyPoliciesProps> = ({
  // TODO hardcoded until we refactor CompanyMembers since have a miniwizard
  // currentStep,
  organization,
  privacyPolicyAccepted,
  currentUser,
  onNextClick,
  isSubmitting,
  claimRequestId,
}) => {
  const { t } = useTranslation();
  const language = getLanguage();

  const [modalVisible, setModalVisible] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [metadata, setMetadata] = useState<any>(null);
  const [documentUploaded, setDocumentUploaded] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [retries, setRetries] = useState(0);

  const token = (Math.random().toString(36).substring(2) + Date.now().toString(36)).toUpperCase();

  const disableSubmitButton = () => {
    let disabled = false;

    if (retries < 3) {
      if (!selectedFile) disabled = true;
      else if (submitting) disabled = true;
      else disabled = false;
    }
    else {
      disabled = false;
    }

    return disabled;
  }

  const handleEntityName = () => {
    if (organization)
      return organization.nombre.length > 30 ? organization.nombre.slice(0, 30) + '...' : organization.nombre;
  };

  const handleModalOpen = () => {
    setModalVisible(true);
  };

  const handleModalClose = () => {
    setModalVisible(false);
  };

  const onComplete = async (data: any) => {
    const confirmButton = document.getElementById('submit_form_button');
    if (confirmButton) {
      confirmButton.scrollIntoView({ behavior: 'smooth' });
      confirmButton.focus();
    }

    setSubmitting(true);
    setMetadata(data);

    await onDownloadDocument(data);
  }

  const onDownloadDocument = async (data: any) => {
    try {
      const body = { external_id: token, submission_id: data.submission_id }
      const res = await apiRequest('/business_claim/download_signed_document', 'POST', body)

      if (res.success) {
        const validRecord = res.response.documents.length > 0;

        if (!validRecord) {
          setRetries(retries + 1);
          if (retries < 3) {
            // wait 5 seconds and try again
            setTimeout(async () => {
              await onDownloadDocument(data);
            }, 5000);
          }
          else {
            setSubmitting(false);
            setDocumentUploaded(false);
    
            handleModalClose();
            throw new Error("No valid documents found.");
          }
        }

        const documentData = res.response.documents[0];

        if (!documentData) {
          setRetries(retries + 1);
        }
        else {
          const pdfUrl = documentData.url;
          
          const response = await fetch(pdfUrl);
          setSubmitting(false);

          if (!response.ok) throw new Error("Failed to download PDF");

          const pdfBlob = await response.blob();
          const pdfFile = new File([pdfBlob], 'politicas_privacidad.pdf', { type: 'application/pdf' });

          setSelectedFile(pdfFile);
        }
      }
    } catch (error) {
      console.error('Error downloading document:', error);
      setSubmitting(false);
      setDocumentUploaded(false);
      handleModalClose();
      throw error;
    }
  }

  const onSubmit = async () => {
    setSubmitting(true);

    const formData = new FormData();
    formData.append('file', selectedFile as Blob);
    formData.append('metadata', JSON.stringify({
      ...metadata, 
      panadata_file_type: 'privacy_policies',
      session_hash: token
    }));
    formData.append('claim_request_id', claimRequestId?.toString() || '');

    try {
      const res = await fetch(`${PANADATA_ROOT}/business_claim/upload_document`, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': getCsrfToken(),
        },
        body: formData,
        credentials: ENVIRONMENT === 'development' ? 'include' : 'same-origin'
      })

      if (!res.ok) {
        throw new Error(`Error en la solicitud, estado ${res.status}`);
      }

      const response = await res.json();

      if (response.success) {
        setDocumentUploaded(true);
        handleModalClose();
        setSubmitting(false);
      } else {
        console.error('Error uploading file:', response.error);
        setSubmitting(false);
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      setSubmitting(false);
      alert('Error uploading file');
    }
  };

  useEffect(() => {
    const modalElement = document.getElementById('documentUploadModal');
    const modalInstance = Modal.getOrCreateInstance(modalElement as Element);

    if (modalVisible) {
      modalInstance.show();
    } 
    else {
      modalInstance.hide();
    }
  }, [modalVisible]);

  return (
    <div className="row d-flex mt-5 justify-content-center" style={{ maxWidth: 1000 }}>
      <div className="w-100 text-center mb-5 position-relative">
        <div className="badge rounded-pill text-uppercase size-12 font-weight-600 w-auto bg-kyb-pill text-dark">
          {handleEntityName()}
        </div>
      </div>

      <span className="size-10 font-weight-600 hyperlink">
        {t('kyb_flow.steps_count', { step: 4, total: 5 })}
      </span>

      <KybDescription
        title={t('kyb_flow.policies.title')}
        description={t('kyb_flow.policies.description')}
      />
      <div className="col-10">
        <div className="mt-5">
          <div className={`card p-2 cursor-pointer ${documentUploaded || privacyPolicyAccepted ? 'kyb-card-selected bg-white' : ''}`} data-testid="operation-permit-card" onClick={handleModalOpen}>
            <div className="card-body p-2">
              <div className="row">
                <div className="col-2 col-md-1 d-flex align-items-center">
                  <div className="kyb-document-icon d-flex justify-content-center align-items-center">
                    <span 
                      className="d-flex align-items-center" 
                      dangerouslySetInnerHTML={{ __html: descriptionIcon }} 
                    />
                  </div>
                </div>
                <div className="col-8">
                  <h5 className="size-14 text-dark font-weight-600 mb-0">{t('kyb_flow.policies.trigger')}</h5>
                  <span className="size-13 fst-italic text-muted">{t('kyb_flow.policies.trigger_description')}</span>
                </div>
                <div className="col-3 d-none d-md-flex justify-content-center align-items-center">
                  {isSubmitting ? (
                    <span 
                      className="d-flex align-items-center hyperlink spin" 
                      dangerouslySetInnerHTML={{ __html: progressIcon }} 
                    />
                  ) : (
                    <div className='d-flex align-items-center justify-content-end cursor-pointer'>
                      <span 
                        className={`d-flex align-items-center ${documentUploaded || privacyPolicyAccepted ? 'text-green me-1' : 'hyperlink'}`} 
                        dangerouslySetInnerHTML={{ __html: documentUploaded || privacyPolicyAccepted ? checkCircleIcon : chevronRightIcon }} 
                      />
                      <span className={`size-12 font-weight-400 ${documentUploaded || privacyPolicyAccepted ? 'text-dark' : 'hyperlink'}`}>
                        {documentUploaded || privacyPolicyAccepted ? t('kyb_flow.policies.policy_accepted') : t('kyb_flow.policies.policy_not_accepted')}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="d-flex flex-column align-items-center">
            <button
              className="btn btn-black w-50 mt-5"
              onClick={() => onNextClick(false)}
              disabled={!documentUploaded && !privacyPolicyAccepted}
            >
              {t('kyb_flow.steps.step_4_button')}
            </button>
          </div>
        </div>

        {/* Modal for uploading documents */}
        <div className="modal fade" id="documentUploadModal" tabIndex={-1} data-bs-backdrop="static" data-bs-keyboard={false} aria-labelledby="document-upload-label" aria-hidden="true">
          <div className="modal-dialog modal-lg" role="document">
            <div className="modal-content">
              {/* <div className="modal-header text-center border-0">
                <h5 className="modal-title w-100 size-20 font-weight-600">{i18n.t('kyb_flow.document_upload.add_document')}</h5>
              </div> */}
              <div className="modal-body border-0 p-3">
                <DocusealForm
                  src="https://docuseal.com/d/FT6wNgQrYsyVdE"
                  externalId={token}
                  email={currentUser.email}
                  minimize={true}
                  withTitle={false}
                  withSendCopyButton={false}
                  withDownloadButton={false}
                  onComplete={onComplete}
                  language={language}
                  customCss={customDocusealCss}
                />
              </div>
              <div className="modal-footer border-0">
                <button 
                  type="button" 
                  className="btn btn-white" 
                  onClick={handleModalClose}
                  data-testid="cancel-modal-button"
                >
                  {t('cancel')}
                </button>
                <button
                  id="submit_form_button"
                  type="button"
                  className="btn btn-black"
                  onClick={onSubmit}
                  disabled={disableSubmitButton()}
                >
                  {submitting ? 
                    <span 
                      className="d-flex align-items-center" 
                      dangerouslySetInnerHTML={{ __html: progressIcon }} 
                    /> :
                    t('confirm')
                  }
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

interface PrivacyPoliciesProps {
  // TODO hardcoded internally until we refactor CompanyMembers since have a miniwizard
  currentStep: number;
  organization: any;
  currentUser: any;
  privacyPolicyAccepted: boolean;
  onNextClick: (skipped: boolean) => void;
  isSubmitting: boolean;
  claimRequestId?: number;
}
